import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Phone as PhoneIcon, Mail, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReviewSection from './ReviewSection';

export default function Layout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Forside', path: '/' },
    { name: 'Salonen', path: '/om-salonen' },
    { name: 'Klipning', path: '/klipning' },
    { name: 'Sauna Udlejning', path: '/sauna-udlejning' },
    { name: 'Produkter', path: '/produkter' },
    { name: 'Priser', path: '/priser' },
    { name: 'Galleri', path: '/galleri' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-stone-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-24">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/24b449b7-8935-4014-a2ff-b9ce3af1c5da.png" 
                  alt="Toftes Hundesalon Logo" 
                  className="h-14 w-auto transition-transform hover:scale-110"
                />
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="hidden min-[1400px]:flex items-center space-x-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[10px] uppercase tracking-[0.3em] font-bold transition-colors hover:text-stone-800 ${
                    location.pathname === link.path ? 'text-stone-800' : 'text-gray-500'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="https://tofteshundesalon.planway.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-taupe-500 text-white px-8 py-3 rounded-none text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-taupe-600 transition-all"
              >
                Book tid
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="min-[1400px]:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-stone-600 p-2"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="min-[1400px]:hidden bg-white border-b border-stone-100 overflow-hidden"
            >
              <div className="px-4 pt-2 pb-10 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-3 py-5 text-xs uppercase tracking-widest font-bold ${
                      location.pathname === link.path
                        ? 'text-stone-800'
                        : 'text-gray-500 hover:text-stone-800'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-6 px-3">
                  <a
                    href="https://tofteshundesalon.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-taupe-500 text-white text-center px-6 py-5 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-taupe-600 transition-all"
                  >
                    Book tid nu
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {location.pathname !== '/produkter' && <ReviewSection />}

      {/* Footer */}
      <footer className="bg-stone-100 text-stone-900 pt-32 pb-16 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-24">
            <div className="space-y-8">
              <Link to="/">
                <img 
                  src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/751ef333-fff5-4650-af56-db08ecfee4bf.png" 
                  alt="Toftes Hundesalon" 
                  className="h-20 w-auto brightness-0 opacity-80 transition-transform hover:scale-110"
                />
              </Link>
              <p className="text-stone-600 leading-relaxed text-sm italic">
                Professionel hundepleje med fokus på tryghed og kvalitet. 
                Din hund er i centrum fra start til slut.
              </p>
              <div className="flex gap-6">
                <a href="https://www.instagram.com/toftes_hundesalon" target="_blank" rel="noopener noreferrer" className="text-stone-800 hover:text-stone-600 transition-colors">
                  <Instagram className="w-6 h-6" />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-10 text-stone-500">Menu</h3>
              <ul className="space-y-5">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-xs uppercase tracking-widest font-bold text-stone-700 hover:text-stone-900 transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-10 text-stone-500">Kontakt</h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-4 text-stone-700">
                  <MapPin className="w-5 h-5 shrink-0 text-stone-500" />
                  <span className="text-sm">Christian x’s vej 264, 8310 Tranbjerg J, Slet</span>
                </li>
                <li className="flex items-center gap-4 text-stone-700">
                  <PhoneIcon className="w-5 h-5 shrink-0 text-stone-500" />
                  <a href="tel:+4528550499" className="text-sm hover:text-stone-900">28 55 04 99</a>
                </li>
                <li className="flex items-center gap-4 text-stone-700">
                  <Mail className="w-5 h-5 shrink-0 text-stone-500" />
                  <a href="mailto:tofteshundesalon@gmail.com" className="text-sm hover:text-stone-900">tofteshundesalon@gmail.com</a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-10 text-stone-500">Booking</h3>
              <p className="text-sm text-stone-600 mb-10 leading-relaxed italic">
                Klar til at give din hund en fantastisk oplevelse? Book din tid online nemt og hurtigt.
              </p>
              <a
                href="https://tofteshundesalon.planway.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-stone-800 text-white px-10 py-4 rounded-none text-[10px] uppercase tracking-widest font-bold hover:bg-stone-900 transition-all"
              >
                Book tid her
              </a>
            </div>
          </div>

          <div className="pt-12 border-t border-stone-200 text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-bold">
              &copy; {new Date().getFullYear()} Toftes Hundesalon v/Helene Tofte Botin Christensen
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
